import { APP_INITIALIZER, EnvironmentProviders, inject, Injectable, Provider } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AuthConfig, OAuthService, provideOAuthClient } from 'angular-oauth2-oidc';
import { envAdmin } from 'src/environments/environment-admin';

@Injectable()
export class AuthenticationService {
  public get username(): string {
    return this.user.name;
  }

  public get accessToken(): string {
    return this.oauthService.getAccessToken();
  }

  private readonly oauthService: OAuthService = inject(OAuthService);
  private readonly router: Router = inject(Router);
  private readonly jwtHelperService = new JwtHelperService();

  private static readonly ORIG_RETURN_URL_KEY = 'ui_rpt_abo_admin_return_url';

  private user: { name: string } = { name: '' };

  /**
   * Initialize the oidc oauth plugin.
   * Loads the Keycloak discovery document and checks if there is already a valid session active.
   * Call this on application startup, best before bootstraping.
   * No actual login is performed.
   */
  public async initializeOidc(): Promise<void> {
    const config: AuthConfig = {
      ...envAdmin.oauth,
      strictDiscoveryDocumentValidation: false,
      skipIssuerCheck: true,
      responseType: 'code',
    };

    this.oauthService.configure(config);
    this.oauthService.setupAutomaticSilentRefresh();
    this.oauthService.sessionChecksEnabled = false;

    if (
      !this.oauthService.hasValidAccessToken() &&
      sessionStorage.getItem(AuthenticationService.ORIG_RETURN_URL_KEY) == null
    ) {
      sessionStorage.setItem(AuthenticationService.ORIG_RETURN_URL_KEY, this.getCurrentLocationPostHash());
    }

    const loggedIn: boolean = await this.oauthService.loadDiscoveryDocumentAndLogin();
    const initialUrl: string | null = sessionStorage.getItem(AuthenticationService.ORIG_RETURN_URL_KEY);

    if (loggedIn && initialUrl !== null) {
      this.updateDataFromToken();
      this.router.navigateByUrl(initialUrl);
      sessionStorage.removeItem(AuthenticationService.ORIG_RETURN_URL_KEY);
    }

    if (this.oauthService.hasValidAccessToken()) {
      this.updateDataFromToken();
    }
  }

  /**
   * Performs a logout for the current user.
   */
  public logout(): void {
    this.oauthService.logOut();
  }

  /**
   * Checks if the current user has a valid access- and id-token.
   * @returns true if user has a valid session.
   */
  public isLoggedIn(): boolean {
    return this.oauthService.hasValidAccessToken() && this.oauthService.hasValidIdToken();
  }

  /**
   * Extract user roles from the current access-token.
   */
  private updateDataFromToken(): void {
    const idToken = this.jwtHelperService.decodeToken(this.oauthService.getIdToken()) as any;
    this.user.name = idToken.name;
  }

  private getCurrentLocationPostHash(): string {
    const hash = window.location.hash;
    if (hash === '') {
      return '/';
    } else {
      return hash.slice(1);
    }
  }
}

export const OAuthProviders: Array<Provider | EnvironmentProviders> = [
  provideOAuthClient(),
  AuthenticationService,
  {
    provide: APP_INITIALIZER,
    useFactory: (authService: AuthenticationService) => async (): Promise<void> => {
      return await authService.initializeOidc();
    },
    deps: [AuthenticationService, Router],
    multi: true,
  },
];
